import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  FaMagic,
  FaCube,
  FaUpload,
  FaUserFriends,
  FaArrowRight,
} from 'react-icons/fa';
import { STANDALONE_IDEAS } from '../data/tattooIdeas';
import Footer from './Footer';
import { SITE_URL, SITE_NAME } from '../constants/site';

// ---------------------------------------------------------------------------
// Content (kept as data so the visible copy and the JSON-LD stay in sync)
// ---------------------------------------------------------------------------
const FEATURES = [
  {
    icon: FaMagic,
    title: 'Generate with AI',
    body: 'Describe your idea and AI sketches it in seconds. Explore variations until it feels right.',
  },
  {
    icon: FaCube,
    title: 'Preview in true 3D',
    body: 'See your design wrapped on a realistic 3D body across 50+ placements. Rotate and scale to know exactly how it sits.',
  },
  {
    icon: FaUpload,
    title: 'Upload your own art',
    body: 'Bring your own artwork or add custom text, then visualize it on the body in real time.',
  },
  {
    icon: FaUserFriends,
    title: 'Find your artist',
    body: 'When it’s ready, browse and connect with tattoo artists to bring it to life.',
  },
];

const STEPS = [
  {
    n: '01',
    title: 'Add your design',
    body: 'Generate one with AI, upload your own art, or add text.',
  },
  {
    n: '02',
    title: 'Place it on the 3D model',
    body: 'Drop it onto any of 50+ body placements and adjust the fit.',
  },
  {
    n: '03',
    title: 'Preview from every angle',
    body: 'Rotate to see how it really looks, then save or share it.',
  },
];

const FAQS = [
  {
    q: 'Is KoiCreate free?',
    a: 'Yes. Creating a free account takes seconds, and designing and previewing tattoos costs nothing. Premium unlocks extras like unlimited layers, premium 3D models and more AI generations.',
  },
  {
    q: 'Can I see what a tattoo looks like on my body?',
    a: 'Yes. Place any design onto a realistic 3D body model and preview it from every angle, so you know exactly how it will sit before you get inked.',
  },
  {
    q: 'How does the 3D tattoo preview work?',
    a: 'Add a design (generate it with AI, upload your own artwork, or add text), drop it onto the 3D model, then rotate, scale and reposition it to see how it wraps around the body in real time.',
  },
  {
    q: 'Can I upload my own tattoo design?',
    a: 'Absolutely. Upload your own artwork and preview it in 3D, or use the AI generator to explore new ideas.',
  },
  {
    q: 'Do I need to download an app?',
    a: 'No. KoiCreate runs right in your browser on desktop and mobile, with nothing to install.',
  },
];

// ---------------------------------------------------------------------------
// Page metadata for '/', consumed by CreateHome's <Seo>. Lives here (not in
// CreateHome) so it stays next to the visible copy it must match.
// ---------------------------------------------------------------------------
export const HOME_TITLE =
  'Free Tattoo Preview & 3D Visualizer | Design Tattoos with AI | KoiCreate';
export const HOME_DESCRIPTION =
  'See your tattoo before you ink it. Design with AI or upload your own art, then preview it on a realistic 3D body from every angle. 100% free.';

export const HOME_JSON_LD = [
  // Note: the WebApplication schema is declared once site-wide in public/index.html.
  // Here we add the page-specific Organization, HowTo and FAQ schema.
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: `${SITE_URL}/`,
    logo: `${SITE_URL}/Logo.png`,
  },
  {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'How to preview a tattoo before you get it',
    step: STEPS.map((s, i) => ({
      '@type': 'HowToStep',
      position: i + 1,
      name: s.title,
      text: s.body,
    })),
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  },
];

// ---------------------------------------------------------------------------
// Top-rated designs — real output from the community, same source /browse
// uses (the previews endpoint orders by likes). Each card deep-links to the
// design's own indexable URL, and the section hides itself if the API fails
// so the page never shows a broken gallery.
// ---------------------------------------------------------------------------
const GALLERY_SIZE = 8;

const TopDesigns = () => {
  const [designs, setDesigns] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    axios
      .get(
        `https://koi-2028.onrender.com/tattoo_designs/previews?limit=${GALLERY_SIZE}`,
        { signal: controller.signal }
      )
      // Slice defensively: older deployed backends ignore `limit` and return
      // the entire table (see the same guard in Home.jsx).
      .then((res) =>
        setDesigns(Array.isArray(res.data) ? res.data.slice(0, GALLERY_SIZE) : [])
      )
      .catch(() => setDesigns([]));
    return () => controller.abort();
  }, []);

  // Nothing yet (still loading) or nothing at all (API down / empty).
  if (!designs || designs.length === 0) return null;

  return (
    <section className="border-y border-white/10 bg-white/[0.02]">
      <div className="mx-auto max-w-7xl px-6 py-20">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-semibold tracking-tight md:text-4xl">
            Tattoos people are previewing right now
          </h2>
          <p className="mt-3 text-lg text-white/60">
            The community’s top-rated designs, straight from the visualizer.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-2 gap-4 md:grid-cols-4">
          {designs.map((design, i) => (
            <Link
              key={design.id}
              to={`/tattoo/${design.id}`}
              className="group overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] transition hover:border-rose-500/50"
            >
              <img
                src={design.preview_image_url}
                alt={`Top-rated tattoo design #${i + 1} previewed on a realistic 3D body model with KoiCreate`}
                className="aspect-[4/3] w-full object-cover transition duration-300 group-hover:scale-[1.03]"
                loading="lazy"
                width="800"
                height="600"
              />
            </Link>
          ))}
        </div>

        <div className="mt-10 text-center">
          <Link
            to="/browse"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-rose-500 to-pink-600 px-7 py-3.5 text-base font-semibold text-white shadow-lg shadow-rose-500/25 transition hover:shadow-xl hover:shadow-rose-500/40 hover:brightness-110"
          >
            Browse all designs <FaArrowRight className="text-sm" />
          </Link>
        </div>
      </div>
    </section>
  );
};

/**
 * Supporting/SEO content rendered on '/' below the create tool. The tool is
 * the hero, so this content explains, proves and deepens rather than sells:
 * bridge intro → how it works → real top-rated designs → features →
 * why-preview editorial → FAQ → idea links. `md:pl-16` clears the fixed app
 * sidebar.
 */
export default function LandingSections() {
  return (
    <div className="bg-gray-800 text-white antialiased md:pl-16">
      {/* ---------- BRIDGE: what this page is, one scroll from the tool ---------- */}
      <section className="relative overflow-hidden border-t border-white/10">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute right-0 top-[-40%] h-[420px] w-[680px] rounded-full bg-rose-600/15 blur-[150px]"
        />
        <div className="relative mx-auto max-w-3xl px-6 py-16 text-center md:py-20">
          <h2 className="text-4xl font-semibold leading-[1.1] tracking-tight md:text-5xl">
            See your tattoo on your body{' '}
            <span className="bg-gradient-to-r from-rose-400 to-pink-500 bg-clip-text text-transparent">
              before you commit.
            </span>
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-lg text-white/60">
            KoiCreate is a free tattoo visualizer. Design with AI or upload your
            own art, then preview it on a realistic 3D body from every angle —
            the tattoo preview tool that removes the guesswork before the ink is
            permanent.
          </p>

          {/* Trust band: proof + facts in one strip */}
          <div className="mt-8 flex flex-col items-center gap-5">
            <div className="flex items-center gap-3">
              <div className="flex shrink-0 -space-x-2.5">
                {[1, 2, 3, 4, 5].map((n) => (
                  <img
                    key={n}
                    src={`/assets/avatars/avatar${n}.jpg`}
                    alt=""
                    aria-hidden="true"
                    className="h-8 w-8 shrink-0 rounded-full object-cover ring-2 ring-gray-800"
                    loading="lazy"
                    width="32"
                    height="32"
                  />
                ))}
              </div>
              <p className="min-w-0 text-sm text-white/60">
                <span className="font-semibold text-white">Join 10,000+</span>{' '}
                people visualizing their next tattoo
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
              {[
                ['50+', 'body placements'],
                ['AI', 'design generator'],
                ['3D', 'true-to-life preview'],
                ['$0', 'free to start'],
              ].map(([big, small]) => (
                <div key={small} className="flex items-baseline gap-1.5">
                  <span className="text-xl font-semibold tracking-tight">{big}</span>
                  <span className="text-sm text-white/50">{small}</span>
                </div>
              ))}
            </div>
            <p className="text-sm text-white/40">
              No credit card · no download · works in your browser.
            </p>
          </div>
        </div>
      </section>

      {/* ---------- HOW IT WORKS ---------- */}
      <section id="how" className="border-t border-white/10 bg-white/[0.02]">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-semibold tracking-tight md:text-4xl">
              Preview a tattoo in three steps
            </h2>
            <p className="mt-3 text-lg text-white/60">
              It takes under a minute, and you can start right now for free.
            </p>
          </div>

          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {STEPS.map((s) => (
              <div key={s.n} className="relative">
                <div className="text-sm font-semibold text-rose-400">{s.n}</div>
                <h3 className="mt-3 text-2xl font-semibold">{s.title}</h3>
                <p className="mt-3 leading-relaxed text-white/60">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- TOP-RATED DESIGNS (real output, links into /browse) ---------- */}
      <TopDesigns />

      {/* ---------- FEATURES ---------- */}
      <section id="features" className="mx-auto max-w-7xl px-6 py-20">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-semibold tracking-tight md:text-4xl">
            Everything you need to design with confidence
          </h2>
          <p className="mt-3 text-lg text-white/60">
            From first idea to a design you can actually picture on your skin.
          </p>
        </div>

        <div className="mt-12 grid gap-4 md:grid-cols-2">
          {FEATURES.map(({ icon: Icon, title, body }) => (
            <div
              key={title}
              className="group rounded-2xl border border-white/10 bg-white/[0.03] p-6 transition hover:border-white/20 hover:bg-white/[0.05]"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-rose-500/20 to-pink-600/20 text-rose-400">
                <Icon className="text-lg" />
              </div>
              <h3 className="mt-5 text-lg font-semibold">{title}</h3>
              <p className="mt-2 leading-relaxed text-white/60">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ---------- WHY PREVIEW (editorial depth) ---------- */}
      <section className="border-y border-white/10 bg-white/[0.02]">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-semibold tracking-tight md:text-4xl">
              Why preview a tattoo before you get inked?
            </h2>
            <p className="mt-3 text-lg text-white/60">
              A tattoo is permanent; your first idea of it doesn’t have to be.
              Most tattoo regret comes down to things you can catch in advance.
            </p>
          </div>

          <div className="mt-12 grid gap-8 md:grid-cols-3">
            <div>
              <h3 className="text-xl font-semibold">Placement changes everything</h3>
              <p className="mt-3 leading-relaxed text-white/60">
                The same design reads completely differently on a forearm, a
                shoulder blade or a calf. A flat sketch can’t show you how the
                artwork wraps around muscle and bone — a 3D tattoo preview can,
                from every angle, before you book the appointment.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold">Size is hard to imagine</h3>
              <p className="mt-3 leading-relaxed text-white/60">
                “A bit bigger” on paper can feel enormous on skin, and fine
                detail that looks great at full size can blur when shrunk down.
                Scaling your design on a realistic body model shows you the
                honest version of both before it’s permanent.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold">Iterate while it’s free</h3>
              <p className="mt-3 leading-relaxed text-white/60">
                Generate variations with AI, compare them side by side, sleep on
                it, and come back tomorrow. When you’ve settled on the one, share
                the preview with your tattoo artist so the consultation starts
                from exactly what you want.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- FAQ ---------- */}
      <section id="faq" className="mx-auto max-w-3xl px-6 py-20">
        <h2 className="text-center text-3xl font-semibold tracking-tight md:text-4xl">
          Frequently asked questions
        </h2>
        <div className="mt-10 divide-y divide-white/10 border-y border-white/10">
          {FAQS.map((f) => (
            <details key={f.q} className="group py-5">
              <summary className="flex cursor-pointer list-none items-center justify-between text-lg font-medium text-white/90 transition hover:text-white">
                {f.q}
                <span className="ml-4 text-white/40 transition group-open:rotate-45">
                  +
                </span>
              </summary>
              <p className="mt-3 leading-relaxed text-white/60">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Explore tattoo ideas — internal links for discovery + SEO */}
      <section className="border-t border-white/10">
        <div className="mx-auto max-w-7xl px-6 py-12">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-white/40">
            Explore tattoo ideas
          </h2>
          <div className="mt-4 flex flex-wrap gap-2.5">
            {STANDALONE_IDEAS.map((idea) => (
              <Link
                key={idea.slug}
                to={`/tattoo-ideas/${idea.slug}`}
                className="rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-sm text-white/70 transition hover:border-white/20 hover:text-white"
              >
                {idea.name} tattoos
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
